import type { TFunction } from "i18next";

import { Stack } from "@mui/material";
import { AutocompleteField } from "components/AutocompleteField";
import { Button } from "components/ButtonNew";
import { ColorInput } from "components/Input";
import { FilterTypes } from "enums/components";
import { StatusChip } from "features/shared/components/StatusChip";
import { getRecordFormFieldLayoutProps } from "features/shared/lib/technical-config-tab/recordFormFields";
import { isNumericGuard } from "utils/dataManipulation";
import { formatNumberBasedOnUser } from "utils/namesManipulation";

import type { ConstructionFormState, LayerFormValue } from "../model/types";
import type { LayeredConstructionVariant } from "../model/variants";

import { LayerCard } from "./LayerCard";

type ConstructionLayersProps = {
  formState: ConstructionFormState;
  materialNames: string[];
  onAddLayer: () => void;
  onColorChange: (color: string) => void;
  onRemoveLayer: (index: number) => void;
  onSelectLayerMaterial: (index: number, materialName: string) => void;
  onSelectType: (typeName: string) => void;
  onUpdateLayer: (index: number, field: keyof LayerFormValue, value: string) => void;
  t: TFunction;
  totalThicknessMillimeters: null | number;
  typeNames: string[];
  uValue: null | number;
  variant: LayeredConstructionVariant;
};

const i18nKey = (variant: LayeredConstructionVariant, key: string) => `TechnicalData.${variant.i18nKey}.${key}`;

export const ConstructionLayers = ({
  formState,
  materialNames,
  onAddLayer,
  onColorChange,
  onRemoveLayer,
  onSelectLayerMaterial,
  onSelectType,
  onUpdateLayer,
  t,
  totalThicknessMillimeters,
  typeNames,
  uValue,
  variant,
}: ConstructionLayersProps) => (
  <Stack className="w-full flex-col items-stretch gap-4">
    <Stack className="w-full flex-row flex-wrap gap-2">
      {isNumericGuard(uValue) ? (
        <StatusChip variant="success">
          {t(i18nKey(variant, "uValueText"), { uValue: formatNumberBasedOnUser(uValue, 2) })}
        </StatusChip>
      ) : undefined}
      {totalThicknessMillimeters ? (
        <StatusChip variant="info">
          {`${formatNumberBasedOnUser(totalThicknessMillimeters, 4)} ${t(i18nKey(variant, "totalThicknessSuffix"))}`}
        </StatusChip>
      ) : undefined}
    </Stack>
    <Stack className="w-full justify-between gap-2">
      <AutocompleteField<string>
        {...getRecordFormFieldLayoutProps(FilterTypes.AUTOCOMPLETE_SINGLE)}
        freeSolo
        inputValue={formState.typeName}
        onChange={(_, value) => onSelectType(typeof value === "string" ? value : String(value ?? ""))}
        onInputChange={(_, value, reason) => reason === "input" && onSelectType(value)}
        options={typeNames}
        placeholder={String(t(i18nKey(variant, "selectType")))}
        value={formState.typeName}
      />
      <ColorInput
        {...getRecordFormFieldLayoutProps(FilterTypes.AUTOCOMPLETE_SINGLE)}
        color={formState.color}
        setColor={onColorChange}
      />
    </Stack>
    <Stack className="w-full flex-row items-center justify-between">
      <span className="uppercase text-lds-foreground-muted font-ds-body-semibold-sm">
        {t(i18nKey(variant, "layers"))}
      </span>
      <Button onClick={onAddLayer} size="sm" startIconName="Add" variant="tertiary">
        {t(i18nKey(variant, "addLayer"))}
      </Button>
    </Stack>
    {formState.layers.length === 0 ? (
      <p className="font-ds-body-regular-sm text-lds-foreground-muted">{t(i18nKey(variant, "noLayers"))}</p>
    ) : (
      formState.layers.map((layer, index) => (
        <LayerCard
          index={index}
          key={index}
          layer={layer}
          materialNames={materialNames}
          onChange={onUpdateLayer}
          onRemove={onRemoveLayer}
          onSelectMaterial={onSelectLayerMaterial}
          variant={variant}
        />
      ))
    )}
  </Stack>
);
